import { TextField, Typography, useTheme } from "@mui/material";
import { useMemo, useState } from "react";

import type { TextComposerCtaButton } from "./ctaSchema";
import { CTA_BUTTON_TEXT_MAX_LENGTH, validateCtaButton } from "./ctaSchema";
import { SectionAccordion } from "../../compose/SectionAccordion";
import type { ExtensionRenderProps } from "../../editor/extensionContract";
import { useMessageDraftStore } from "../../editor/messageDraftStoreFactory";
import { InlineLexicalInput } from "../../InlineLexicalInput";
import { getUrlMergeTagPickerConfig } from "../mergeTags/mergeTagDefinitions";

import { CheckCircleIcon as CheckCircle, BoltIcon } from "@/icons";

export function CTAButton({
  authoringState,
  disabled = false,
}: ExtensionRenderProps) {
  const theme = useTheme();
  const setCtaButton = useMessageDraftStore(
    (s) =>
      s.actions.setCtaButton as (
        updates: Partial<TextComposerCtaButton>,
      ) => void,
  );
  const hadCtaOnLoad = useMessageDraftStore(
    (s) => s.initialAuthoringState.ctaButton !== undefined,
  );
  const [expanded, setExpanded] = useState(false);

  const ctaButton = useMemo(
    () => authoringState.ctaButton ?? { buttonText: "", buttonUrl: "" },
    [authoringState.ctaButton],
  );
  const hasContent =
    ctaButton.buttonText.trim().length > 0 ||
    ctaButton.buttonUrl.trim().length > 0;
  const errors = useMemo(
    () => (hasContent ? validateCtaButton(ctaButton) : {}),
    [ctaButton, hasContent],
  );

  const charCount = ctaButton.buttonText.length;
  const buttonTextHelperText =
    errors.buttonText ?? `${charCount}/${CTA_BUTTON_TEXT_MAX_LENGTH}`;

  const urlMergeTags = useMemo(() => getUrlMergeTagPickerConfig(), []);

  if (!hadCtaOnLoad) return null;

  return (
    <SectionAccordion
      title="CTA Button"
      icon={BoltIcon}
      chips={hasContent ? <CheckCircle color="primary" /> : undefined}
      summaryId="text-composer-cta-header"
      detailsId="text-composer-cta-content"
      expanded={expanded}
      disabled={disabled}
      onExpandedChange={setExpanded}
    >
      <Typography variant="body3" color="text.secondary">
        Button Text
      </Typography>
      <TextField
        placeholder="e.g. SHOP NOW"
        value={ctaButton.buttonText}
        onChange={(event) => setCtaButton({ buttonText: event.target.value })}
        variant="outlined"
        size="small"
        fullWidth
        disabled={disabled}
        error={errors.buttonText !== undefined}
        helperText={hasContent ? buttonTextHelperText : undefined}
        slotProps={{
          input: {
            sx: {
              backgroundColor: "background.paper",
              fontSize: theme.typography.body2.fontSize,
            },
          },
        }}
        // `&&` outweighs the global MuiOutlinedInput theme border override.
        sx={{
          "&& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
          "&&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "divider",
          },
        }}
        inputProps={{ "data-1p-ignore": true }}
      />

      <Typography variant="body3" color="text.secondary">
        Button URL
      </Typography>
      <InlineLexicalInput
        value={ctaButton.buttonUrl}
        onChange={(value) => setCtaButton({ buttonUrl: value })}
        surface="textComposer"
        allowedTags={urlMergeTags.allowedTags}
        extraCategories={urlMergeTags.extraCategories}
        placeholder="e.g. https://www.your-site.com"
        editable={!disabled}
        error={errors.buttonUrl !== undefined}
        helperText={errors.buttonUrl}
      />
    </SectionAccordion>
  );
}
